import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
    ReactNode,
} from "react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "./AuthContext";

interface Notification {
    id: string | number;
    timestamp: string | number | Date;
    is_read: boolean;
    [key: string]: any;
}

interface NotificationContextType {
    notifications: Notification[];
    setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
    unreadCount: number;
    markAsRead: (id: any) => void;
    refreshNotifications: () => void;
    connectionStatus: string;
    error: any;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
    undefined
);
interface NotificationProviderProps {
    children: ReactNode;
}
export const NotificationProvider = ({
    children,
}: NotificationProviderProps) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState("disconnected");
    const [error, setError] = useState<any>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const pingIntervalRef = useRef(null);
    const isMountedRef = useRef(false);
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_DELAY = 5000;
    const contextData = useAuth();
    const user = contextData.user.user_id;

    const baseUrl = process.env.REACT_APP_WS_URL;

    const updateUnreadCount = useCallback((notifs: any[]) => {
        const count = notifs.filter((n) => !n.is_read).length;
        setUnreadCount(count);
    }, []);

    const handleNewNotifications = useCallback(
        (data: any) => {
            if (!isMountedRef.current) return;
            // The service sends created_at (not timestamp): an invalid date made
            // formatDistanceToNow throw, so no notification was ever stored.
            const formattedNotifications = (Array.isArray(data) ? data : [data])
                .filter((notification) => notification && notification.id)
                .map((notification) => {
                    const timestamp = notification.timestamp || notification.created_at || new Date().toISOString();
                    const date = new Date(timestamp);
                    return {
                        ...notification,
                        timestamp,
                        timeAgo: Number.isNaN(date.getTime()) ? "" : formatDistanceToNow(date),
                        isNew: !notification.is_read,
                    };
                });

            setNotifications((prev) => {
                const newNotifications = formattedNotifications.filter(
                    (newNotif) => !prev.some((existing) => existing.id === newNotif.id)
                );
                const merged = [...newNotifications, ...prev];

                return merged;
            });

            updateUnreadCount(formattedNotifications);
        },
        [updateUnreadCount]
    );

    const cleanupWebSocket = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }

        if (socketRef.current) {
            socketRef.current.onopen = null;
            socketRef.current.onmessage = null;
            socketRef.current.onerror = null;
            socketRef.current.onclose = null;

            if (socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.close();
            }

            socketRef.current = null;
        }
    }, []);

    const getAuthToken = useCallback((): string | null => {
        const tokenData = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
        if (!tokenData) return null;
        try {
            const parsed = JSON.parse(tokenData);
            return parsed.access || parsed.access_token || parsed.token || tokenData;
        } catch {
            return tokenData;
        }
    }, []);

    const connectWebSocket = useCallback(() => {
        if (
            !isMountedRef.current ||
            reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS
        ) {
            return;
        }

        cleanupWebSocket();
        setConnectionStatus("connecting");

        const token = getAuthToken();
        if (!token || !baseUrl) {
            setConnectionStatus("error");
            setError("Auth token or WebSocket URL is not defined.");
            console.error("[NotificationContext] Auth token or WebSocket URL is undefined.");
            return;
        }

        // Pass the auth token via query parameter (browser WebSocket API doesn't support custom headers)
        const wsUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
        socketRef.current = new WebSocket(wsUrl);

        socketRef.current.onopen = () => {
            if (!isMountedRef.current) return;

            reconnectAttemptsRef.current = 0;
            setConnectionStatus("connected");

            if (notifications.length === 0) {
                if (socketRef.current) {
                    socketRef.current.send(
                        JSON.stringify({ type: "request_notifications" })
                    );
                }
            }
        };

        socketRef.current.onmessage = (event) => {
            if (!isMountedRef.current) return;

            try {
                const message = JSON.parse(event.data);

                console.log("Message", message);

                switch (message.type) {
                    case "notifications":
                        handleNewNotifications(message.data);
                        break;
                    case "notification":
                        handleNewNotifications(message.data);
                        break;
                    case "new_notification":
                        handleNewNotifications(message.data);
                        break;
                    case "read_confirmation":
                        if (message.success) {
                            setNotifications((prev) =>
                                prev.filter((n) => n.id !== message.id)
                            );
                            setUnreadCount((prev) => prev - 1);
                        }
                        break;
                    case "heartbeat":
                        if (socketRef.current?.readyState === WebSocket.OPEN) {
                            socketRef.current.send(
                                JSON.stringify({ type: "heartbeat", message: "pong" })
                            );
                        }
                        break;
                    case "error":
                        setError(message.message);
                        console.error(
                            "[NotificationContext] WebSocket error:",
                            message.message
                        );
                        break;
                    default:
                        console.log(
                            "[NotificationContext] Unknown message type:",
                            message.type
                        );
                        break;
                }
            } catch (err) {
                console.error("[NotificationContext] Error processing message:", err);
            }
        };

        socketRef.current.onerror = () => {
            if (!isMountedRef.current) return;
            setConnectionStatus("error");
            console.error("[NotificationContext] WebSocket error");
        };

        socketRef.current.onclose = (event) => {
            if (!isMountedRef.current) return;

            if (!event.wasClean) {
                setConnectionStatus("disconnected");
                reconnectAttemptsRef.current += 1;
                const delay = Math.min(
                    RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
                    60000
                );
                console.warn(
                    "[NotificationContext] WebSocket closed unexpectedly. Reconnecting in",
                    delay,
                    "ms"
                );
                setTimeout(connectWebSocket, delay);
            } else {
                console.log("[NotificationContext] WebSocket closed cleanly");
            }
        };
    }, [
        cleanupWebSocket,
        getAuthToken,
        handleNewNotifications,
        notifications.length,
        baseUrl,
    ]);

    const markAsRead = useCallback((id: any) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log("[NotificationContext] Marking notification as read:", id);
            socketRef.current.send(
                JSON.stringify({
                    type: "mark_as_read",
                    notification_id: id,
                })
            );
        }
    }, []);

    const refreshNotifications = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log("[NotificationContext] Refreshing notifications");
            socketRef.current.send(JSON.stringify({ type: "request_notifications" }));
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        console.log("[NotificationContext] useEffect mount, user:", user);

        if (user) {
            connectWebSocket();
        }

        return () => {
            isMountedRef.current = false;
            cleanupWebSocket();
            console.log("[NotificationContext] useEffect cleanup");
        };
    }, [user, connectWebSocket, cleanupWebSocket]);

    const value = {
        notifications,
        setNotifications,
        unreadCount,
        connectionStatus,
        error,
        markAsRead,
        refreshNotifications,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error(
            "useNotifications must be used within a NotificationProvider"
        );
    }
    return context;
};


